import axios from 'axios';
import { format } from 'date-fns';
import type {
  NewAccountsReportItem,
  PaysReportItem,
  ReceivedPaysReportItem,
} from '@/models/report/types';
import {
  buildNewClientsExportData,
  buildPaysExportData,
  buildReceivedPaysExportData,
  type CountryOf,
  type ExportRow,
  type ReportExportData,
} from '@/lib/reportExportData';

const GOOGLE_IDENTITY_SCRIPT_ID = 'google-identity-services';
const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const GOOGLE_SHEETS_SCOPE = 'https://www.googleapis.com/auth/spreadsheets';
const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GooglePopupError {
  type: 'popup_closed' | 'popup_failed_to_open' | 'unknown';
}

interface GoogleTokenClient {
  requestAccessToken: (overrideConfig?: { prompt?: string }) => void;
}

interface GoogleOAuth {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: GoogleTokenResponse) => void;
    error_callback?: (error: GooglePopupError) => void;
  }) => GoogleTokenClient;
}

interface GoogleIdentity {
  accounts?: {
    oauth2?: GoogleOAuth;
  };
}

interface CreateSpreadsheetResponse {
  spreadsheetId: string;
  spreadsheetUrl?: string;
  sheets?: Array<{
    properties?: {
      sheetId?: number;
    };
  }>;
}

interface GoogleApiErrorResponse {
  error?: {
    message?: string;
    status?: string;
  };
}

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      GOOGLE_IDENTITY_SCRIPT_ID
    ) as HTMLScriptElement | null;

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Не удалось загрузить Google Identity Services')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_IDENTITY_SCRIPT_ID;
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Не удалось загрузить Google Identity Services'));
    document.head.appendChild(script);
  });
}

async function requestAccessToken(): Promise<string> {
  if (!googleClientId) {
    throw new Error('Google экспорт не настроен: не задан VITE_GOOGLE_CLIENT_ID');
  }

  await loadGoogleIdentityScript();

  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) {
    throw new Error('Google Identity Services недоступен');
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error('Время ожидания авторизации истекло. Попробуйте ещё раз.'));
    }, 90_000);

    const tokenClient = oauth.initTokenClient({
      client_id: googleClientId,
      scope: GOOGLE_SHEETS_SCOPE,
      callback: (response) => {
        clearTimeout(timeout);

        if (response.error) {
          const noAccess =
            response.error === 'access_denied' || response.error === 'popup_closed_by_user';
          reject(
            new Error(
              noAccess
                ? 'Нет доступа к Google Sheets. Запросите разрешение у администратора Google Workspace.'
                : (response.error_description ?? response.error)
            )
          );
          return;
        }

        if (!response.access_token) {
          reject(new Error('Google не вернул access token'));
          return;
        }

        resolve(response.access_token);
      },
      error_callback: (error) => {
        clearTimeout(timeout);
        if (error.type === 'popup_closed') {
          reject(
            new Error(
              'Нет доступа к Google Sheets. Запросите разрешение у администратора Google Workspace.'
            )
          );
        } else if (error.type === 'popup_failed_to_open') {
          reject(
            new Error('Не удалось открыть окно авторизации. Разрешите всплывающие окна в браузере.')
          );
        } else {
          reject(new Error('Ошибка авторизации Google. Попробуйте ещё раз.'));
        }
      },
    });

    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
}

function escapeSheetName(sheetName: string): string {
  return sheetName.replace(/'/g, "''");
}

function buildValues(report: ReportExportData): (string | number)[][] {
  const toValues = (row: ExportRow) => report.columns.map((column) => row[column.key] ?? '');
  return [
    report.columns.map((column) => column.header),
    ...report.rows.map(toValues),
    ...(report.totals.length > 0 ? [[], ...report.totals.map(toValues)] : []),
  ];
}

async function createSpreadsheet(
  report: ReportExportData,
  accessToken: string
): Promise<CreateSpreadsheetResponse> {
  const title = `${report.title} ${format(new Date(), 'yyyy-MM-dd')}`;
  const { data } = await axios.post<CreateSpreadsheetResponse>(
    'https://sheets.googleapis.com/v4/spreadsheets',
    {
      properties: { title },
      sheets: [{ properties: { title: report.sheetName } }],
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  return data;
}

async function writeSpreadsheet(
  report: ReportExportData,
  spreadsheetId: string,
  accessToken: string
) {
  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values:batchUpdate`,
    {
      valueInputOption: 'USER_ENTERED',
      data: [
        {
          range: `'${escapeSheetName(report.sheetName)}'!A1`,
          majorDimension: 'ROWS',
          values: buildValues(report),
        },
      ],
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
}

async function styleSpreadsheet(
  report: ReportExportData,
  spreadsheetId: string,
  sheetId: number,
  accessToken: string
) {
  const rowCount = report.rows.length + 1;
  // Итоги идут после пустой строки под таблицей.
  const totalsStart = rowCount + 1;
  const totalsEnd = totalsStart + report.totals.length;
  const columnCount = report.columns.length;
  const moneyColumnIndexes = report.columns
    .map((column, index) => (column.type === 'money' ? index : -1))
    .filter((index) => index >= 0);

  await axios.post(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}:batchUpdate`,
    {
      requests: [
        {
          updateSheetProperties: {
            properties: {
              sheetId,
              gridProperties: {
                frozenRowCount: 1,
              },
            },
            fields: 'gridProperties.frozenRowCount',
          },
        },
        {
          repeatCell: {
            range: {
              sheetId,
              startRowIndex: 0,
              endRowIndex: 1,
              startColumnIndex: 0,
              endColumnIndex: columnCount,
            },
            cell: {
              userEnteredFormat: {
                backgroundColor: { red: 0.2039, green: 0.1804, blue: 0.2157 },
                horizontalAlignment: 'LEFT',
                verticalAlignment: 'MIDDLE',
                textFormat: {
                  foregroundColor: { red: 1, green: 1, blue: 1 },
                  bold: true,
                  fontSize: 11,
                },
              },
            },
            fields:
              'userEnteredFormat(backgroundColor,textFormat,horizontalAlignment,verticalAlignment)',
          },
        },
        {
          updateDimensionProperties: {
            range: {
              sheetId,
              dimension: 'ROWS',
              startIndex: 0,
              endIndex: 1,
            },
            properties: {
              pixelSize: 28,
            },
            fields: 'pixelSize',
          },
        },
        {
          autoResizeDimensions: {
            dimensions: {
              sheetId,
              dimension: 'COLUMNS',
              startIndex: 0,
              endIndex: columnCount,
            },
          },
        },
        {
          updateDimensionProperties: {
            range: {
              sheetId,
              dimension: 'COLUMNS',
              startIndex: 0,
              endIndex: columnCount,
            },
            properties: {
              pixelSize: 160,
            },
            fields: 'pixelSize',
          },
        },
        {
          addBanding: {
            bandedRange: {
              range: {
                sheetId,
                startRowIndex: 0,
                endRowIndex: rowCount,
                startColumnIndex: 0,
                endColumnIndex: columnCount,
              },
              rowProperties: {
                headerColor: { red: 0.2039, green: 0.1804, blue: 0.2157 },
                firstBandColor: { red: 0.9804, green: 1, blue: 0.9922 },
                secondBandColor: { red: 0.9412, green: 0.9333, blue: 0.949 },
              },
            },
          },
        },
        ...moneyColumnIndexes.map((index) => ({
          repeatCell: {
            range: {
              sheetId,
              startRowIndex: 1,
              endRowIndex: report.totals.length > 0 ? totalsEnd : rowCount,
              startColumnIndex: index,
              endColumnIndex: index + 1,
            },
            cell: {
              userEnteredFormat: {
                numberFormat: {
                  type: 'NUMBER',
                  pattern: '#,##0.00',
                },
                horizontalAlignment: 'RIGHT',
              },
            },
            fields: 'userEnteredFormat(numberFormat,horizontalAlignment)',
          },
        })),
        ...(report.totals.length > 0
          ? [
              {
                repeatCell: {
                  range: {
                    sheetId,
                    startRowIndex: totalsStart,
                    endRowIndex: totalsEnd,
                    startColumnIndex: 0,
                    endColumnIndex: columnCount,
                  },
                  cell: { userEnteredFormat: { textFormat: { bold: true } } },
                  fields: 'userEnteredFormat.textFormat.bold',
                },
              },
            ]
          : []),
      ],
    },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
}

function getExportErrorMessage(error: unknown): string {
  if (axios.isAxiosError<GoogleApiErrorResponse>(error)) {
    if (error.response?.status === 403) {
      return 'Нет доступа к Google Sheets API. Запросите разрешение у администратора Google Workspace.';
    }
    return error.response?.data?.error?.message ?? error.message;
  }

  return error instanceof Error ? error.message : 'Не удалось выполнить экспорт в Google Sheets';
}

async function exportReportToGoogleSheets(report: ReportExportData) {
  try {
    const accessToken = await requestAccessToken();
    const spreadsheet = await createSpreadsheet(report, accessToken);
    await writeSpreadsheet(report, spreadsheet.spreadsheetId, accessToken);
    const sheetId = spreadsheet.sheets?.[0]?.properties?.sheetId;

    if (sheetId !== undefined) {
      await styleSpreadsheet(report, spreadsheet.spreadsheetId, sheetId, accessToken);
    }

    const url =
      spreadsheet.spreadsheetUrl ??
      `https://docs.google.com/spreadsheets/d/${spreadsheet.spreadsheetId}`;

    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    throw Object.assign(new Error(getExportErrorMessage(error)), { cause: error });
  }
}

export async function exportNewClientsToGoogleSheets(
  items: NewAccountsReportItem[],
  countryOf: CountryOf<NewAccountsReportItem>
) {
  await exportReportToGoogleSheets(buildNewClientsExportData(items, countryOf));
}

export async function exportPaysToGoogleSheets(
  items: PaysReportItem[],
  countryOf: CountryOf<PaysReportItem>
) {
  await exportReportToGoogleSheets(buildPaysExportData(items, countryOf));
}

export async function exportReceivedPaysToGoogleSheets(
  items: ReceivedPaysReportItem[],
  countryOf: CountryOf<ReceivedPaysReportItem>
) {
  await exportReportToGoogleSheets(buildReceivedPaysExportData(items, countryOf));
}
